import {
	CheckboxItem,
	ExtendedListLRSelectItemOne,
	ExtendedLRSelectItem,
	LRRadio,
	RadioItem,
	RadioSelectItem,
	TypedFactory
} from '@/store/prescription/definition/categories/prescriptionCategoriesDefinition';
import { Accommodations, Forefoot, ForefootPostValue, LROptions, RearfootPost, ShellModifications } from '@/store/prescription/prescription-types';
import PrescriptionItemSelectLR from '@/components/new-prescription/prescription-items/PrescriptionItemSelectLR.vue';

const post = { '0deg': '0 °', '1deg': '1 °', '2deg': '2 °', '3deg': '3 °', '4deg': '4 °', '5deg': '5 °', '6deg': '6 °' };

const heelCushion: RadioSelectItem<LROptions, NonNullable<Accommodations['heelCushion']['value']>> = {
	description: `Heel cushions are thin pads
that are placed into the heel
cup region of the orthoses.
They are beveled along the
forward edge and are used to
provide an additional layer of
cushioning directly underneath
the calcaneus (heel bone).
Deeper heel seats are often
used in conjunction with Heel
Cushions.`,
	display: 'Heel Cushion',
	imgPath: '/assets/images/prescriptions/Heel Cushion.svg',
	radioValues: {
		...LRRadio
	},
	selectValues: { '1.5mm': '1.5mm', '3mm': '3mm' }
};

const mortonsExtensionEva: RadioItem<LROptions> = {
	description:
		"Morton’s Extensions are made from 3mm EVA. They are placed at the medial distal edge of the orthoses and run under the hallux until they cross the interphalangeal joint. The Morton's Extension is helpful in treating patients with plantar 2nd metatarsal head symptoms and pain associated with hallux limitus but it will not impede forward propulsion during gait.",
	display: "Morton's Ext (EVA)",
	imgPath: "/assets/images/prescriptions/Morton's Ext (EVA).svg",
	radioValues: {
		...LRRadio
	}
};

const dispersionPadHallux: RadioItem<LROptions> = {
	description:
		'Dispersion Pads are made from 3mm EVA and are placed under the 1st metatarsal head. They are used to treat plantar 1st metatarsal head symptoms and pain associated with hallux limitus. They are also used to treat plantar 1st metatarsal head ulcers.',
	display: 'Dispersion Pad Hallux',
	imgPath: '/assets/images/prescriptions/dispersion_pad_at_hallux.svg',
	radioValues: {
		...LRRadio
	}
};

const dispersionPadAtNavicular: RadioItem<LROptions> = {
	description: 'Dispersion Pads are made from 3mm EVA and are placed under navicular. They are used to offload pain under the arch.',
	display: 'Dispersion Pad At Navicular',
	imgPath: '/assets/images/prescriptions/Dispersion_pad_at_navicular.svg',
	radioValues: {
		...LRRadio
	}
};

const dispersionPadAtStyloidProcess: RadioItem<LROptions> = {
	description: `Dispersion Pads are made from 3mm EVA and are placed under navicular. They are used to offload pain under the base of the 5th.`,
	display: 'Dispersion Pad At Styloid Process',
	imgPath: '/assets/images/prescriptions/dispersion_pad_styloid_process.svg',
	radioValues: {
		...LRRadio
	}
};

const forefootExtension: RadioSelectItem<LROptions, NonNullable<Accommodations['forefootExtension']['value']>> = {
	description: `Forefoot Extensions are 3mm EVA that begin just
proximal to the distal edge of the shell and
extends to the toes. They are used to provide
additional cushion in the forefoot without adding
bulk underneath the entire foot.`,
	display: 'Forefoot Extension (EVA)',
	imgPath: '/assets/images/prescriptions/forefoot_extension.svg',
	radioValues: {
		...LRRadio
	},
	selectValues: { '1.5mm': '1.5mm', '3mm': '3mm' }
};

const heelSpurPad: RadioSelectItem<LROptions, NonNullable<Accommodations['heelSpurPad']['value']>> = {
	description:
		'Heel Spur Pad is a horseshoe “U” shaped pad made from foam material and placed on top of the shell in the heel cup area. It’s purpose is to off-weight the center of the heel, provide additional shock absorption and to gather the fat pad under the heel. Deeper heel seats are often used in conjunction with heel spur pads.',
	display: 'Heel Spur Pad',
	imgPath: '/assets/images/prescriptions/Heel Spur Pad.svg',
	radioValues: {
		...LRRadio
	},
	selectValues: { '1.5mm': '1.5mm', '3mm': '3mm' }
};

const metBar: RadioSelectItem<LROptions, NonNullable<Accommodations['metBar']['value']>> = {
	description:
		'Metatarsal Bars are pads made from foam materials that are placed behind the ball of the foot, usually behind the 2nd, 3rd and 4th metatarsal joint heads. They are used to reduce pain in the forefoot and work by reducing the pressure and shearing forces that take place at the MPJ heads and by transferring weight away from sore areas and painful calluses.',
	display: 'Met Bar',
	imgPath: '/assets/images/prescriptions/Met Bar.svg',
	radioValues: {
		...LRRadio
	},
	selectValues: { '1.5mm': '1.5mm', '3mm': '3mm' }
};

const dancerPad: RadioSelectItem<LROptions, NonNullable<Accommodations['dancerPad']['value']>> = {
	description:
		'Dancer’s Pad is made from foam materials and is placed behind the ball of the foot. The pad’s primary thickness is behind the 2nd and 3rd MPJ’s with a cut out for the 1st MPJ. This design transfers weight from the 1st MPJ to the lesser metatarsals making it effective in treating painful conditions of the 1st MPJ.',
	display: "Dancer's Pad",
	imgPath: "/assets/images/prescriptions/Dancer's Pad.svg",
	radioValues: {
		...LRRadio
	},
	selectValues: { '1.5mm': '1.5mm', '3mm': '3mm' }
};

const metPads: RadioSelectItem<LROptions, NonNullable<Accommodations['metPads']['value']>> = {
	description: `Metatarsal pads are made from
foam materials that are placed
behind the ball of the foot, usually
behind the 2nd, 3rd and 4th
metatarsal joint heads. They are
used to reduce pain in the forefoot
and work by elevating the
metatarsal bones and reducing
the impact and shearing forces
that take place at the MPJ heads
and by transferring weight away
from sore areas and painful
calluses.`,
	display: 'Metatarsal Pads',
	imgPath: '/assets/images/prescriptions/Met Pads.svg',
	radioValues: {
		...LRRadio
	},
	selectValues: { '1.5mm': '1.5mm', '3mm': '3mm', '5mm': '5mm' }
};

const neuromaPad: CheckboxItem = {
	checkbox: ['1', '2', '3', '4'],
	description:
		'Neuroma pads are small pads made from foam materials that are placed between metatarsal heads. They are used to treat neuromas and other painful conditions which are caused by nerve tissue entrapment between the metatarsal bones. By separating these bones, pain, numbness and tingling can often be eliminated.',
	display: 'Neuroma Pad',
	imgPath: '/assets/images/prescriptions/Neuroma Pad.svg'
};

const balancePad: CheckboxItem = {
	checkbox: ['1', '2', '3', '4', '5'],
	description:
		'Balance Pads are made from foam materials and are designed to take the weight off of painful metatarsal joints on the bottom of the foot. They are often shaped liked a “U” and are designed to elevate the two adjacent MPJ’s while leaving the area under the painful MPJ empty or in a “floating” state.\n',
	display: 'Balance Pad',
	imgPath: '/assets/images/prescriptions/Met Head Cut Out.svg'
};

const extrinsicForefootPost: ExtendedLRSelectItem<'none' | 'balance-to-rearfoot' | 'specify', Forefoot['type'], ForefootPostValue> = {
	description: `The Forefoot Post tilts either the medial or lateral front edge of the orthoses to the ground. Forefoot Posts can be either Varus or Valgus.\n\nAn Intrinsic forefoot post manipulates the angle of the distal end of the shell. An Extrinsic forefoot post is created by adding a wedge under the distal end of the orthotic shell during post-production. All orthoses made with an extrinsic post will automatically be balanced forefoot to rearfoot. This means that the distal aspect of the orthotic that sits under the metatarsals will be made flat to so that there will be no rocking of the foot orthotic in the shoe.`,
	display: 'Extrinsic Forefoot Post (EVA)',
	imgPath: '/assets/images/prescriptions/Forefoot Post.svg',
	selectValues: {
		none: 'N/A',
		'balance-to-rearfoot': 'Balance to Rearfoot',
		specify: 'Specify Values'
	},
	LR: {
		firstValue: {
			varus: 'Varus',
			valgus: 'Valgus'
		},
		secondValue: {
			...post
		}
	}
};

const accommodationsSpecialRX: string[] = [];

const typeFactory = new TypedFactory<Accommodations>();
export default typeFactory.typeItems({
	heelCushion,
	mortonsExtensionEva,
	heelSpurPad,
	metBar,
	dancerPad,
	metPads,
	neuromaPad,
	balancePad,
	accommodationsSpecialRX,
	forefootExtension,
	dispersionPadHallux,
	dispersionPadAtNavicular,
	dispersionPadAtStyloidProcess,
	extrinsicForefootPost
});
